/**
 * Cross-domain OAuth callback bounce — shared across integration providers.
 *
 * Every integration OAuth provider (Meta/WhatsApp, Shopify, …) registers a
 * SINGLE redirect URL on the canonical app host (`app.panluma.ai`), so a grant
 * started on a sysadmin- or custom-domain always returns to the canonical host.
 * That admin has no tenant session there (auth tokens are per-origin
 * localStorage), so the tenant-scoped completion must run back on the
 * originating domain.
 *
 * The backend recorded the originating origin in the provider's own one-time
 * OAuth state at `begin` time; `resolveOrigin` reads it back (server-trusted — a
 * forged/expired state has no entry → no bounce, so this is NOT an open
 * redirect). If it differs from the current origin we forward the still-
 * unexchanged OAuth response (`forwardQuery`) to the origin domain's own callback
 * route, where the admin's session lives. Mirrors the SSO cross-domain pattern
 * (docs/platform/oauth-sso-design.md) and shared.utils.oauth_origin (backend).
 */

import axios from 'axios';
import { API_BASE_URL } from '../api/config';

/**
 * Interceptor-free client for the PUBLIC cross-domain OAuth origin/resume lookups.
 * These run on the canonical app host where the admin may have no (or a stale)
 * session; the authed `axiosInstance` would proactively refresh/expire that token
 * and could clear storage + emit SESSION_EXPIRED mid-bounce. This ONE instance is
 * shared by every provider's `resolveOAuthOrigin` (Google/Microsoft/Notion/Stripe/
 * Shopify) and Plaid's resume lookup, so the rationale lives in a single place.
 */
export const publicAxios = axios.create({ baseURL: API_BASE_URL });

function normalizeOrigin(origin: string): string {
  return origin.replace(/\/+$/, '').toLowerCase();
}

/**
 * If the connect flow must continue on a different (originating) domain, navigate
 * there and return true (the caller should stop — the page is unloading).
 * Returns false when the flow should proceed on the current domain (same origin,
 * state expired, or the lookup failed — degrade to same-domain completion).
 *
 * `forwardQuery` is the query string (with or without a leading `?`) carried to
 * the origin domain's `/oauth/callback/{provider}` route. Providers whose
 * callback authentication spans several params (Shopify's `shop` + `hmac`) pass
 * the full `window.location.search` so the signature still verifies downstream.
 */
export async function bounceOAuthCallbackToOrigin(opts: {
  provider: string;
  forwardQuery: string;
  resolveOrigin: () => Promise<string | null>;
}): Promise<boolean> {
  let origin: string | null;
  try {
    origin = await opts.resolveOrigin();
  } catch {
    // Lookup failed — fall back to completing on the current domain.
    return false;
  }
  if (!origin || normalizeOrigin(origin) === normalizeOrigin(window.location.origin)) {
    return false;
  }
  const query = opts.forwardQuery.startsWith('?') ? opts.forwardQuery : `?${opts.forwardQuery}`;
  window.location.assign(`${origin.replace(/\/+$/, '')}/oauth/callback/${opts.provider}${query}`);
  return true;
}
